import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { Drawer } from '@/shared/ui/organisms/Drawer';
import type { NotificationItem } from '../../../types/notification';
import { NotificationMobilePreview } from '../../molecules/NotificationMobilePreview';

export interface NotificationDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  item: NotificationItem | null;
}

/**
 * Drawer de detalhes de uma notificação.
 *
 * Layout em duas colunas: esquerda com "Conteúdo Enviado" e "Resumo",
 * direita com pré-visualização mobile do push.
 *
 * @param open - Visibilidade do drawer
 * @param onClose - Callback de fechamento
 * @param item - Notificação a exibir; null não renderiza conteúdo
 */
export function NotificationDetailDrawer({ open, onClose, item }: NotificationDetailDrawerProps) {
  const { t } = useTranslation('notification');

  if (!item) return null;

  // const _stats = item.stats;

  return (
    <Drawer open={open} onClose={onClose} subtitle={item.sentAt} title={item.title}>
      <div className="flex gap-sm">
        {/* Left column */}
        <div className="flex min-w-none flex-1 flex-col gap-xs">
          {/* Conteúdo Enviado */}
          <div>
            <p className="ts-heading-xs mb-micro tracking-wide text-ctx-content-title">
              {t('detail.content.sectionTitle')}
            </p>
            <div className="rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini">
              <p className="ts-paragraph-xs mb-quark tracking-wide text-ctx-content-base">
                {t('detail.content.titleLabel')}
              </p>
              <p className="ts-paragraph-xs mb-mini break-words text-ctx-content-title">
                {item.title}
              </p>
              <p className="ts-paragraph-xs mb-quark tracking-wide text-ctx-content-base">
                {t('detail.content.bodyLabel')}
              </p>
              <p className="ts-paragraph-xs break-words leading-relaxed text-ctx-content-title">
                {item.preview}
              </p>
              {item.urlExternal && (
                <>
                  <p className="ts-paragraph-xs mb-quark tracking-wide text-ctx-content-base mt-mini">
                    {t('detail.content.urlLabel')}
                  </p>
                  <p className="ts-paragraph-xs break-all leading-relaxed text-ctx-content-title">
                    {item.urlExternal}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Resumo */}
          <div>
            <p className="ts-subtitle-xs mb-micro tracking-wide text-ctx-content-title">
              {t('detail.stats.summary')}
            </p>

            <p className="ts-paragraph-xs mb-micro text-ctx-content-base">
              {t('detail.stats.createdBy')} {item.sender.name}.
            </p>

            {/* Enviado para */}
            {item.status === 'delivered' && (
              <div className="mb-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini">
                <div className="flex items-start justify-between">
                  <p className="ts-caption-xxxs text-ctx-content-base">
                    {t('detail.stats.sentTo')}
                  </p>
                  <div className="flex h-xs w-xs items-center justify-center rounded-circular bg-base-brand-200">
                    <PaperAirplaneIcon className="h-mini w-mini text-base-brand-600 " />
                  </div>
                </div>
                <div className="flex flex-row gap-nano align-bottom">
                  <p className="ts-heading-md text-ctx-content-title">{item.studentCount}</p>
                  <p className="ts-paragraph-xs text-ctx-content-base pt-quark">
                    {t('detail.stats.studentsUnit')}
                  </p>
                </div>
              </div>
            )}

            {/* Falhas + Clicou */}
            {/* <div className="mb-micro grid grid-cols-2 gap-micro">
              <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini">
                <div className="mb-quark flex items-start justify-between">
                  <p className="ts-subtitle-xxxs text-ctx-content-base">
                    {t("detail.stats.failures")}
                  </p>
                  <div className="flex h-xs w-xs items-center justify-center rounded-circular bg-base-warning-100">
                    <ExclamationTriangleIcon className="h-mini w-mini text-base-warning-600" />
                  </div>
                </div>
                <p className="ts-heading-xs text-ctx-content-title">
                  {stats ? stats.notDeliveredCount : t("detail.stats.noData")}
                </p>
                {stats && (
                  <p className="ts-paragraph-xs text-ctx-content-base">
                    {t("detail.stats.studentsUnit")}
                  </p>
                )}
              </div>

              <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini">
                <div className="mb-quark flex items-start justify-between">
                  <p className="ts-subtitle-xxxs text-ctx-content-base">
                    {t("detail.stats.clicked")}
                  </p>
                  <div className="flex h-xs w-xs items-center justify-center rounded-circular bg-base-success-100">
                    <CheckCircleIcon className="h-mini w-mini text-base-success-600" />
                  </div>
                </div>
                <p className="ts-heading-xs text-ctx-content-title">
                  {stats ? stats.openCount : t("detail.stats.noData")}
                </p>
                {stats && (
                  <p className="ts-paragraph-xs text-ctx-content-base">
                    {t("detail.stats.studentsUnit")}
                  </p>
                )}
              </div>
            </div> */}

            {/* Turmas */}
            {item.classrooms.length > 0 && (
              <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface">
                <p className="ts-subtitle-xxxs border-b-hairline border-ctx-layout-border px-mini py-nano text-ctx-content-base">
                  {t('detail.stats.classrooms')} ({item.classrooms.length})
                </p>
                <div className="max-h-[10rem] overflow-y-auto">
                  <ul>
                    {item.classrooms.map(name => (
                      <li
                        key={name}
                        className="ts-paragraph-xs border-b-hairline border-ctx-layout-border px-mini py-nano text-ctx-content-base last:border-b-none"
                      >
                        {name}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right column — phone preview */}
        <div className="flex shrink-0 flex-col">
          <NotificationMobilePreview
            heading={t('detail.preview.sectionTitle')}
            title={item.title}
            body={item.preview}
          />
        </div>
      </div>
    </Drawer>
  );
}
